import React from "react";
import type { ReactElement } from "react";

import type { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import type { StackNavigationProp } from "@react-navigation/stack";

import type { HubTabNavigatorProps } from "pianofunclub-crm/navigation/HubNavigator";
import defaultNavOptions from "pianofunclub-crm/navigation/utils/defaultNavigationOptions";
import type {
  AccountParams,
  DefaultTermDatesParams,
  EnrolmentParams,
  InvoiceParams,
  PayslipParams,
  SchoolParams,
} from "pianofunclub-crm/navigation/utils/screenParams";
import AccountScreen, {
  screenOptions as accountScreenOptions,
} from "pianofunclub-crm/screens/accounts/AccountScreen";
import InvoiceScreen, {
  screenOptions as invoiceScreenOptions,
} from "pianofunclub-crm/screens/invoicing/InvoiceScreen";
import PayratesScreen, {
  screenOptions as payratesScreenOptions,
} from "pianofunclub-crm/screens/payroll/PayratesScreen";
import PayrollHubScreen, {
  screenOptions as payrollHubScreenOptions,
} from "pianofunclub-crm/screens/payroll/PayrollHubScreen";
import PayslipScreen, {
  screenOptions as payrollScreenOptions,
} from "pianofunclub-crm/screens/payroll/PayslipScreen";
import DefaultTermDatesScreen, {
  screenOptions as defaultTermDatesScreenOptions,
} from "pianofunclub-crm/screens/schools/DefaultTermDatesScreen";
import SchoolScreen, {
  screenOptions as schoolScreenOptions,
} from "pianofunclub-crm/screens/schools/SchoolScreen";
import EnrolmentScreen, {
  screenOptions as enrolmentScreenOptions,
} from "pianofunclub-crm/screens/waitingList/EnrolmentScreen";

export type PayrollNavigatorParamList = {
  Account: AccountParams;
  DefaultTermDates: DefaultTermDatesParams;
  Enrolment: EnrolmentParams;
  Invoice: InvoiceParams;
  Payrates: undefined;
  PayrollHub?: {
    month: number;
    orderBy?: string;
    searchTerm?: string;
    year: number;
  };
  Payslip: PayslipParams;
  School: SchoolParams;
};

type NavigatorProps<T extends keyof PayrollNavigatorParamList> = StackNavigationProp<
  PayrollNavigatorParamList,
  T
>;

export type PayrollStackNavigatorProps<T extends keyof PayrollNavigatorParamList> =
  CompositeNavigationProp<NavigatorProps<T>, HubTabNavigatorProps<"PayrollHubTab">>;

export type PayrollStackRouteProps<T extends keyof PayrollNavigatorParamList> = RouteProp<
  PayrollNavigatorParamList,
  T
>;

const PayrollStackNavigator = createStackNavigator<PayrollNavigatorParamList>();

const PayrollNavigator = (): ReactElement => {
  return (
    <PayrollStackNavigator.Navigator screenOptions={defaultNavOptions}>
      <PayrollStackNavigator.Screen
        component={PayrollHubScreen}
        name="PayrollHub"
        options={payrollHubScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={PayslipScreen}
        name="Payslip"
        options={payrollScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={PayratesScreen}
        name="Payrates"
        options={payratesScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={SchoolScreen}
        name="School"
        options={schoolScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={AccountScreen}
        name="Account"
        options={accountScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={InvoiceScreen}
        name="Invoice"
        options={invoiceScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={DefaultTermDatesScreen}
        name="DefaultTermDates"
        options={defaultTermDatesScreenOptions}
      />
      <PayrollStackNavigator.Screen
        component={EnrolmentScreen}
        name="Enrolment"
        options={enrolmentScreenOptions}
      />
    </PayrollStackNavigator.Navigator>
  );
};

export default PayrollNavigator;
